import { inject, Injectable } from '@angular/core'
import { Actions, ofType, createEffect } from '@ngrx/effects'
import { EMPTY, of } from 'rxjs'
import {
  catchError,
  map,
  switchMap,
  distinctUntilChanged,
  tap,
  mapTo,
  expand,
  reduce,
  scan,
  last,
} from 'rxjs/operators'
import * as adminActions from './admin.actions'
import { EmptyAction } from '..'
import { AdminService } from '../../services/admin.services'
import { IBook, IChapter, ILayoutList, IPage } from '../../models/admin'
import { SnackBarService } from '../../services/snackbar.service'
import { Store } from '@ngrx/store'
import { ActivatedRoute, Router } from '@angular/router'
import { UserService } from '../../services/user.service'

@Injectable()
export class AdminEffects {
  private actions$ = inject(Actions)
  private adminService = inject(AdminService)
  private userService = inject(UserService);
  private snackBarService = inject(SnackBarService);
  private store = inject(Store);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  getEditorBooks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(adminActions.GetEditorBooksAction),
      switchMap(() =>
        this.adminService.getEditorBooks(1).pipe(
          expand(response =>
            (response.TotalPages && response.PageNumber && response.TotalPages > response.PageNumber) // Check if more pages exist
              ? this.adminService.getEditorBooks(response.PageNumber + 1) // Fetch next page
              : EMPTY
          ),
          scan((acc, response) => ({
            ...response,
            Value: [...acc.Value, ...response.Value],
          })),
          last(),
          map(finalResponse =>
            adminActions.GetEditorBooksSuccessAction({ response: finalResponse })
          ),
          catchError(error =>
            of(adminActions.GetEditorBooksFailureAction({ error }))
          )
        )
      )
    )
  );

  deleteBook$ = createEffect(() =>
    this.actions$.pipe(
      ofType(adminActions.DeleteBookAction),
      switchMap(({ bookId }) => {
        return this.adminService.deleteBook(bookId).pipe(
          map((res) => adminActions.DeleteBookSuccessAction({ response: res })),
          catchError((error) =>
            of(adminActions.DeleteBookFailureAction({ response: error })),
          ),
        )
      }),
    ),
  )

  addBook$ = createEffect(() =>
    this.actions$.pipe(
      ofType(adminActions.AddBookAction),
      switchMap((props) => {
        return this.adminService.addBookWithChapters(props.book).pipe(
          map((res) =>
            adminActions.AddBookSuccessAction({ response: res })
          ),
          catchError((error) =>
            of(adminActions.AddBookFailureAction({ error: error.error })),
          ),
        )
      }),
    ),
  )

  addBookSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(adminActions.AddBookSuccessAction),
      switchMap((props) => {
        this.snackBarService.showSnackBar("success", "Book Added SuccessFully");
        this.router.navigate([`/admin/books`]);
        return of(EmptyAction());
      })
    )
  );

  addBookFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(adminActions.AddBookFailureAction),
      tap(({ error }) => {
        console.error(error);
        this.snackBarService.showSnackBar('error', error.Errors[0].Message || "Adding Book Failed");
      }),
      mapTo(EmptyAction())
    )
  );

  updateBook$ = createEffect(() =>
    this.actions$.pipe(
      ofType(adminActions.UpdateBookAction),
      switchMap((props) => {
        return this.adminService.updateBook(props.bookId, props.updatedFields).pipe(
          map((res) =>
            adminActions.UpdateBookSuccessAction({ response: res })
          ),
          catchError((error) =>
            of(adminActions.UpdateBookFailureAction({ error: error.error })),
          ),
        )
      }),
    ),
  )

  updateBookSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(adminActions.UpdateBookSuccessAction),
      switchMap((props) => {
        this.snackBarService.showSnackBar("success", "Book Updated SuccessFully");
        // Dispatch Fetch Editor Books Here
        this.store.dispatch(adminActions.GetEditorBooksAction());
        return of(EmptyAction());
      })
    )
  );

  updateBookFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(adminActions.UpdateBookFailureAction),
      tap(({ error }) => {
        console.error(error);
        this.snackBarService.showSnackBar('error', error.Errors[0].Message);
      }),
      mapTo(EmptyAction())
    )
  );

  deleteBookSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(adminActions.DeleteBookSuccessAction),
      map(() => {
        this.snackBarService.showSnackBar("success", "Book Deleted SuccessFully");
        return adminActions.GetEditorBooksAction();
      }),
    ),
  )

  // Chapter effects
  getEditorChapters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(adminActions.GetEditorChaptersAction),
      switchMap(({ bookId }) => {
        //true is for for book
        return this.adminService.getEditorChaptersWithSubchapters(bookId).pipe(
          map((res) =>
            adminActions.GetEditorChaptersSuccessAction({
              parentId: bookId,
              chapters: res.Value,
            }),
          ),
          catchError((error) =>
            of(adminActions.GetEditorChaptersFailureAction({ error })),
          ),
        )
      }),
    ),
  )

  deleteChapter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(adminActions.DeleteChapterAction),
      switchMap(({ bookId, chapterId }) => {
        return this.adminService.deleteChapter(chapterId).pipe(
          map(() => adminActions.DeleteChapterSuccessAction({ chapterId })),
          catchError((error) =>
            of(adminActions.DeleteChapterFailureAction({ error })),
          ),
        )
      }),
    ),
  )

  getEditorPages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(adminActions.GetEditorPagesAction),
      switchMap(({ chapterId }) => {
        return this.adminService.getEditorPages(chapterId, 1).pipe(
          expand(response =>
            (response.TotalPages && response.PageNumber && response.TotalPages > response.PageNumber) // Check if more pages exist
              ? this.adminService.getEditorPages(chapterId, response.PageNumber + 1) // Fetch next page
              : EMPTY
          ),
          scan((acc, response) => ({
            ...response,
            Value: [...acc.Value, ...response.Value],
          })),
          last(),
          map(finalResponse =>
            adminActions.GetEditorPagesSuccessAction({
              chapterId,
              pages: finalResponse.Value,
            })
          ),
          catchError(error =>
            of(adminActions.GetEditorPagesFailureAction({ error }))
          )
        )
      }),
    ),
  )

  addPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(adminActions.AddPageAction),
      switchMap(({ chapterId, page }) => {
        return this.adminService.addPage(chapterId, page).pipe(
          map((res) =>
            adminActions.AddPageSuccessAction({
              response: {
                Value: res.Value,
                errors: [],
                chapterId,
              },
            }),
          ),
          catchError((error) =>
            of(adminActions.AddPageFailureAction({ response: error })),
          ),
        )
      }),
    ),
  )

  // // Add success handler to refresh pages // no needed just update the state locally in reducer
  // addPageSuccess$ = createEffect(() =>
  //     this.actions$.pipe(
  //         ofType(adminActions.AddPageSuccessAction),
  //         map(({ response }) => adminActions.GetEditorPagesAction({
  //             chapterId: response.chapterId
  //         }))
  //     )
  // );

  deletePage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(adminActions.DeletePageAction),
      switchMap(({ chapterId, pageId }) => {
        return this.adminService.deletePage(chapterId, pageId).pipe(
          map(() => adminActions.DeletePageSuccessAction({ pageId: pageId, chapterId: chapterId })),
          catchError((err) =>
            of(adminActions.DeletePageFailureAction({ error: err.error })),
          ),
        )
      }),
    ),
  )

  deletePageSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(adminActions.DeletePageSuccessAction),
      switchMap((props) => {
        this.snackBarService.showSnackBar('success', "Page Deleted Sucessfully.");
        // Fetch the New Pages After The Page Has Been Deleted
        this.store.dispatch(adminActions.GetEditorPagesAction({ chapterId: props.chapterId }));
        return of(EmptyAction());
      })
    ),
  )

  deletePageFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(adminActions.DeletePageFailureAction),
      tap(({ error }) => {
        this.snackBarService.showSnackBar('error', "Deleting Page Failed: " + error?.Errors[0]?.Message || "");
      }),
      mapTo(EmptyAction())
    ),
  )

  updatePage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(adminActions.UpdatePageAction),
      switchMap(({ page }) => {
        return this.adminService.updatePage(page).pipe(
          map((res) => {
            return adminActions.UpdatePageSuccessAction({ page: {...res.Value, chapterId: page.chapterId} })
          }),
          catchError((err) =>
            of(adminActions.UpdatePageFailureAction({ error: err.error })),
          ),
        )
      }),
    ),
  )

  updatePageSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(adminActions.UpdatePageSuccessAction),
      switchMap((props) => {
        this.snackBarService.showSnackBar('success', "Page Updated Sucessfully.");
        // Fetch the New Pages After The Page Has Been Updated
        this.store.dispatch(adminActions.GetEditorPagesAction({ chapterId: props.page.chapterId }));
        return of(EmptyAction());
      })
    ),
  )

  updatePageFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(adminActions.UpdatePageFailureAction),
      tap(({ error }) => {
        this.snackBarService.showSnackBar('error', "Updating Page Failed: " + error?.Errors[0]?.Message || "");
      }),
      mapTo(EmptyAction())
    ),
  )

  //layout related effects

  // getLayouts$ = createEffect(() => {
  //     return this.actions$.pipe(
  //         ofType(adminActions.GetLayoutsAction),
  //         // Add distinctUntilChanged to prevent duplicate requests
  //         distinctUntilChanged(),
  //         switchMap(() => {
  //             return this.adminService.getLayouts().pipe(
  //                 map((res) => {
  //                     console.log("map",res);
  //                     return adminActions.GetLayoutsSuccessAction({ layouts: res.Value })
  //         }),
  //                 catchError(error => of(adminActions.GetLayoutsFailureAction({ error })))
  //             );
  //         })
  //     )
  // });

  getLayouts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(adminActions.GetLayoutsAction),
      distinctUntilChanged(),
      switchMap(() => {
        return this.adminService.getLayouts().pipe(
          map((res) =>
            adminActions.GetLayoutsSuccessAction({ layouts: [...res.Value] }),
          ),
          catchError((error) =>
            of(adminActions.GetLayoutsFailureAction({ error })),
          ),
        )
      }),
    ),
  )

  addGlobalLayout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(adminActions.AddGlobalLayoutAction),
      switchMap(({ layout }) => {
        return this.adminService.addLayout(layout).pipe(
          map((res) =>
            adminActions.AddGlobalLayoutSuccessAction({ layout: res.Value as ILayoutList }),
          ),
          catchError((error) =>
            of(adminActions.AddGlobalLayoutFailureAction({ error })),
          ),
        )
      }),
    ),
  )

  deleteLayout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(adminActions.DeleteLayoutAction),
      switchMap(({ layoutId }) => {
        return this.adminService.deleteLayout(layoutId).pipe(
          map(() => adminActions.DeleteLayoutSuccessAction({ layoutId })),
          catchError((error) =>
            of(
              adminActions.DeleteLayoutFailureAction({
                response: {
                  Value: {
                    layoutId,
                  },
                  errors: error,
                },
              }),
            ),
          ),
        )
      }),
    ),
  )

  fetchSubChapters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(adminActions.FetchSubChaptersAction),
      switchMap((props) => {
        return this.adminService.getEditorChapters(props.chapterId, false).pipe(
          map((res) =>
            adminActions.FetchSubChaptersSuccesAction({ response: res, chapterId: props.chapterId }),
          ),
          catchError((error) =>
            of(adminActions.FetchSubChaptersFailureAction({ error: error.error })),
          ),
        )
      }),
    ),
  )

  fetchSubChaptersSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(adminActions.FetchSubChaptersSuccesAction),
      switchMap((props) => {
        return of(EmptyAction());
      })
    )
  );

  fetchSubChaptersFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(adminActions.FetchSubChaptersFailureAction),
      tap(({ error }) => {
        console.error(error);
        this.snackBarService.showSnackBar('error', error.Errors[0].Message);
      }),
      mapTo(EmptyAction())
    )
  );


  updateChapter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(adminActions.UpdateChapterAction),
      switchMap((props) => {
        return this.adminService.updateChapter(props.chapterId, props.updatedFields).pipe(
          map((res) =>
            adminActions.UpdateChapterSuccessAction({ response: res, parentChapterId: props.parentChapterId }),
          ),
          catchError((error) =>
            of(adminActions.updateChapterFailureAction({ error: error.error })),
          ),
        )
      }),
    ),
  )

  updateChapterSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(adminActions.UpdateChapterSuccessAction),
      switchMap((props) => {
        this.snackBarService.showSnackBar("success", "Chapter Updated SuccessFully");
        if (props.parentChapterId) {
          this.store.dispatch(adminActions.FetchSubChaptersAction({ chapterId: props.parentChapterId }));
        }
        return of(EmptyAction());
      })
    )
  );

  updateChapterFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(adminActions.updateChapterFailureAction),
      tap(({ error }) => {
        console.error(error);
        this.snackBarService.showSnackBar('error', error.Errors[0].Message);
      }),
      mapTo(EmptyAction())
    )
  );

  // Add SubChapter
  addSubChapter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(adminActions.AddSubChapterAction),
      switchMap((props) => {
        return this.adminService.postEditorChapter(props.chapterId, props.subChapterInfo, false).pipe(
          map((res) =>
            adminActions.AddSubChapterSuccessAction({ response: res, parentChapterId: props.chapterId })
          ),
          catchError((error) =>
            of(adminActions.AddSubChapterFailureAction({ error: error.error })),
          ),
        )
      }),
    ),
  )

  // Add SubChapter Success
  addSubChapterSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(adminActions.AddSubChapterSuccessAction),
      switchMap((props) => {
        this.snackBarService.showSnackBar("success", "SubChapter Added SuccessFully");
        this.router.navigate(['admin/books/chapters', props.parentChapterId, 'subchapters']);
        this.store.dispatch(adminActions.FetchSubChaptersAction({ chapterId: props.parentChapterId }));
        return of(EmptyAction());
      })
    )
  );

  // Add SubChapter Failure
  addSubChapterFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(adminActions.AddSubChapterFailureAction),
      tap(({ error }) => {
        console.error(error);
        this.snackBarService.showSnackBar('error', error.Errors[0].Message);
      }),
      mapTo(EmptyAction())
    )
  );

  // Delete SubChapter
  deleteSubChapter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(adminActions.DeleteSubChapterAction),
      switchMap((props) => {
        return this.adminService.deleteSubChapter(props.subChapterId).pipe(
          map((res) =>
            adminActions.DeleteSubChapterSuccesAction({ response: res, parentChapterId: props.parentChapterId })
          ),
          catchError((error) =>
            of(adminActions.DeleteSubChapterFailureAction({ error: error.error })),
          ),
        )
      }),
    ),
  )

  // Delete SubChapter Success
  deleteSubChapterSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(adminActions.DeleteSubChapterSuccesAction),
      switchMap((props) => {
        this.snackBarService.showSnackBar("success", "SubChapter Deleted SuccessFully");
        this.store.dispatch(adminActions.FetchSubChaptersAction({ chapterId: props.parentChapterId }));
        return of(EmptyAction());
      })
    )
  );

  // Delete SubChapter Failure
  deleteSubChapterFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(adminActions.DeleteSubChapterFailureAction),
      tap(({ error }) => {
        console.error(error);
        this.snackBarService.showSnackBar('error', error.Errors[0].Message);
      }),
      mapTo(EmptyAction())
    )
  );

  // Release Book
  releaseBook$ = createEffect(() =>
    this.actions$.pipe(
      ofType(adminActions.ReleaseBookAction),
      switchMap((props) => {
        return this.adminService.releaseBook(props.bookId).pipe(
          map((res) =>
            adminActions.ReleaseBookSuccessAction({ response: res }),
          ),
          catchError((error) =>
            of(adminActions.ReleaseBookFailureAction({ error: error.error })),
          ),
        )
      }),
    ),
  )

  // Release Book Success
  releaseBookSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(adminActions.ReleaseBookSuccessAction),
      switchMap((props) => {
        const currentUrl = this.router.url;
        const adminBookRegex = /^\/admin\/books\/[a-f0-9\-]{36}$/;
        // Show the Release Book Notification Only on Book Edit Page
        if (adminBookRegex.test(currentUrl)) {
          this.snackBarService.showSnackBar('success', "Book released successfully.");
        }
        return of(EmptyAction());
      })
    )
  );

  // Release Book Failure
  releaseBookFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(adminActions.ReleaseBookFailureAction),
      tap(({ error }) => {
        console.error(error);
        this.snackBarService.showSnackBar('error', error.Errors[0].Message);
      }),
      mapTo(EmptyAction())
    )
  );


  // Fetch All Orders
  fetchAllOrders$ = createEffect(() =>
    this.actions$.pipe(
      ofType(adminActions.AdminFetchOrdersAction),
      switchMap((props) => {
        return this.userService.getAllOrders(props.request.PageNumber, props.request.PageSize, props.request.OrderStatus,
          props.request.PaymentStatus, props.request.Search, props.request.PlanId, props.request.DateFrom, props.request.DateTo).pipe(
            map((res) => {
              return adminActions.AdminFetchOrdersSuccessAction({
                response: res,
                paymentStatus: props.request.PaymentStatus,
                orderStatus: props.request.OrderStatus,
                search: props.request.Search,
                planid: props.request.PlanId,
                dateFrom: props.request.DateFrom,
                dateTo: props.request.DateTo
              })
            }),
            catchError(err => {
              return of(adminActions.AdminFetchOrdersFailureAction({
                error: err.error
              }))
            })
          );
      })
    )
  );

  // Fetch All Orders Success
  fetchAllOrdersSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(adminActions.AdminFetchOrdersSuccessAction),
      switchMap((props) => {
        return of(EmptyAction());
      })
    )
  );

  // Fetch All Orders Failure
  fetchAllOrdersFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(adminActions.AdminFetchOrdersFailureAction),
      switchMap((props) => {
        this.snackBarService.showSnackBar('error', props?.error?.Errors?.[0]?.Message || 'Fetching Orders Failed.');
        return of(EmptyAction());
      })
    )
  );
}
